import React, { useState, useEffect } from "react";
import {
  Table,
  Card,
  Button,
  Modal,
  Form,
  Select,
  Input,
  Tag,
  Space,
  message,
  Row,
  Col,
  Statistic,
  Descriptions,
  Tabs,
  DatePicker,
  Popconfirm,
  Badge,
  Tooltip,
  Empty
} from "antd";
import {
  EyeOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  UserAddOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  PhoneOutlined,
  CalendarOutlined,
  TeamOutlined,
  RiseOutlined
} from "@ant-design/icons";
import courseService from "../../services/course.service";

const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

const InstructorRequestManager = () => {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [statusUpdateVisible, setStatusUpdateVisible] = useState(false);
//   const [availableStatuses, setAvailableStatuses] = useState({});
  const [searchFilters, setSearchFilters] = useState({});
  const [form] = Form.useForm();

  useEffect(() => {
    loadRequests();
    loadStats();
    // loadAvailableStatuses();
  }, []);

  const availableStatuses = {
  PENDING: "En attente",
  UNDER_REVIEW: "En cours d'évaluation",
  CONTACTED: "Contacté",
  INTERVIEW_SCHEDULED: "Entretien programmé",
  APPROVED: "Approuvé",
  REJECTED: "Rejeté",
  ONBOARDING: "En cours d'intégration",
  ACTIVE: "Actif",
};

  const loadRequests = async () => {
    setLoading(true);
    try {
      const data = await courseService.getAllInstructorRequests();
      setRequests(data);
    } catch (error) {
      message.error("Erreur lors du chargement des demandes");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await courseService.getInstructorStats();
      setStats(data);
    } catch (error) {
      console.error("Erreur lors du chargement des statistiques:", error);
    }
  };



  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setDetailsVisible(true);
  };

  const handleStatusUpdate = (request) => {
    setSelectedRequest(request);
    form.setFieldsValue({
      status: request.status,
      adminNotes: request.adminNotes || "",
    });
    setStatusUpdateVisible(true);
  };

  const handleStatusSubmit = async () => {
    try {
      const values = await form.validateFields();
      await courseService.updateInstructorStatusWithNotes(selectedRequest.id, {
        status: values.status,
        adminNotes: values.adminNotes,
      });
      message.success("Statut mis à jour avec succès");
      setStatusUpdateVisible(false);
      loadRequests();
      loadStats();
    } catch (error) {
      message.error("Erreur lors de la mise à jour du statut");
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await courseService.deleteInstructorRequest(id);
      message.success("Demande supprimée avec succès");
      loadRequests();
      loadStats();
    } catch (error) {
      message.error("Erreur lors de la suppression");
      console.error(error);
    }
  };

  const handleSearch = async (filters) => {
    setLoading(true);
    try {
      const data = await courseService.advancedInstructorSearch(filters);
      setRequests(data.content || data);
    } catch (error) {
      message.error("Erreur lors de la recherche");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      PENDING: "orange",
      UNDER_REVIEW: "blue",
      CONTACTED: "cyan",
      INTERVIEW_SCHEDULED: "purple",
      APPROVED: "green",
      REJECTED: "red",
      ONBOARDING: "geekblue",
      ACTIVE: "success",
    };
    return colors[status] || "default";
  };

  const getStatusIcon = (status) => {
    const icons = {
      PENDING: <ClockCircleOutlined />,
      UNDER_REVIEW: <SearchOutlined />,
      CONTACTED: <PhoneOutlined />,
      INTERVIEW_SCHEDULED: <CalendarOutlined />,
      APPROVED: <CheckCircleOutlined />,
      REJECTED: <CloseCircleOutlined />,
      ONBOARDING: <RiseOutlined />,
      ACTIVE: <UserAddOutlined />,
    };
    return icons[status] || null;
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 60,
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (date) => new Date(date).toLocaleDateString("fr-FR"),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: "Candidat",
      key: "candidate",
      width: 200,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: "bold" }}>
            {record.firstName} {record.lastName}
          </div>
          <div style={{ fontSize: "12px", color: "#999" }}>
            {record.email}
          </div>
        </div>
      ),
    },
    {
      title: "Téléphone",
      dataIndex: "phone",
      key: "phoneNumber",
      width: 120,
    },
    // {
    //   title: "Domaine d'expertise",
    //   dataIndex: "subjectExpertise",
    //   key: "subjectExpertise",
    //   ellipsis: true,
    //   width: 180,
    // },
    // {
    //   title: "Expérience",
    //   dataIndex: "yearsOfExperience",
    //   key: "yearsOfExperience",
    //   width: 100,
    //   render: (years) => `${years} ans`,
    // },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      width: 180,
      render: (status) => (
        <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
          {availableStatuses[status] || status}
        </Tag>
      ),
      filters: Object.keys(availableStatuses).map((key) => ({
        text: availableStatuses[key],
        value: key,
      })),
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "Actions",
      key: "actions",
      width: 200,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Voir les détails">
            <Button
              type="default"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          <Tooltip title="Changer le statut">
            <Button
              type="primary"
              size="small"
              onClick={() => handleStatusUpdate(record)}
            >
              Statut
            </Button>
          </Tooltip>
          <Popconfirm
            title="Êtes-vous sûr de vouloir supprimer cette demande ?"
            onConfirm={() => handleDelete(record.id)}
            okText="Oui"
            cancelText="Non"
          >
            <Tooltip title="Supprimer">
              <Button danger size="small" icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      {/* Statistiques */}
      {stats && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card
              bordered={false}
              style={{
                borderLeft: "4px solid #1890ff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              <Statistic
                title="Total Demandes"
                value={stats.totalRequests || 0}
                prefix={<TeamOutlined style={{ color: "#1890ff" }} />}
                valueStyle={{ color: "#1890ff" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card
              bordered={false}
              style={{
                borderLeft: "4px solid #faad14",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              <Statistic
                title="En Attente"
                value={stats.pendingCount || 0}
                prefix={<ClockCircleOutlined style={{ color: "#faad14" }} />}
                valueStyle={{ color: "#faad14" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card
              bordered={false}
              style={{
                borderLeft: "4px solid #52c41a",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              <Statistic
                title="Approuvées"
                value={stats.approvedCount || 0}
                prefix={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
                valueStyle={{ color: "#52c41a" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card
              bordered={false}
              style={{
                borderLeft: "4px solid #f5222d",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              <Statistic
                title="Rejetées"
                value={stats.rejectedCount || 0}
                prefix={<CloseCircleOutlined style={{ color: "#f5222d" }} />}
                valueStyle={{ color: "#f5222d" }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Boutons d'action */}
      <Card
        style={{ marginBottom: 16 }}
        bodyStyle={{ padding: "12px 16px" }}
      >
        <Space>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={loadRequests}
            loading={loading}
          >
            Actualiser
          </Button>
        </Space>
      </Card>

      {/* Tableau principal */}
      <Card bordered={false} style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
        <Table
          columns={columns}
          dataSource={requests}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1400 }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} demandes`,
          }}
        />
      </Card>

      {/* Modal de détails */}
      <Modal
        title={
          <div style={{ fontSize: "18px", fontWeight: "bold" }}>
            Détails de la candidature
          </div>
        }
        open={detailsVisible}
        onCancel={() => setDetailsVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailsVisible(false)}>
            Fermer
          </Button>,
          <Button
            key="update"
            type="primary"
            onClick={() => {
              setDetailsVisible(false);
              handleStatusUpdate(selectedRequest);
            }}
          >
            Modifier le statut
          </Button>,
        ]}
        width={800}
      >
        {selectedRequest && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="ID" span={2}>
              {selectedRequest.id}
            </Descriptions.Item>
            <Descriptions.Item label="Nom complet" span={2}>
              {selectedRequest.fullName} {selectedRequest.lastName}
            </Descriptions.Item>
            <Descriptions.Item label="Email" span={2}>
              {selectedRequest.email}
            </Descriptions.Item>
            <Descriptions.Item label="Téléphone" span={2}>
              {selectedRequest.phone}
            </Descriptions.Item>
            {/* <Descriptions.Item label="Domaine d'expertise" span={2}>
              {selectedRequest.subjectExpertise}
            </Descriptions.Item> */}
            <Descriptions.Item label="Années d'expérience" span={1}>
              {selectedRequest.yearsOfExperience} ans
            </Descriptions.Item>
            <Descriptions.Item label="Statut" span={1}>
              <Tag
                color={getStatusColor(selectedRequest.status)}
                icon={getStatusIcon(selectedRequest.status)}
              >
                {availableStatuses[selectedRequest.status] ||
                  selectedRequest.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Date de candidature" span={2}>
              {new Date(selectedRequest.createdAt).toLocaleString("fr-FR")}
            </Descriptions.Item>
            {selectedRequest.updatedAt && (
              <Descriptions.Item label="Dernière mise à jour" span={2}>
                {new Date(selectedRequest.updatedAt).toLocaleString("fr-FR")}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Message de motivation" span={2}>
              {selectedRequest.message || "Aucun message"}
            </Descriptions.Item>
           
           // Dans le Modal de détails, après "Message de motivation"
{selectedRequest.linkedinProfile && (
  <Descriptions.Item label="Profil LinkedIn" span={2}>
    <a href={selectedRequest.linkedinProfile} target="_blank" rel="noopener noreferrer">
      {selectedRequest.linkedinProfile}
    </a>
  </Descriptions.Item>
)}

{selectedRequest.portfolio && (
  <Descriptions.Item label="Portfolio" span={2}>
    <a href={selectedRequest.portfolio} target="_blank" rel="noopener noreferrer">
      {selectedRequest.portfolio}
    </a>
  </Descriptions.Item>
)}

{selectedRequest.reviewedAt && (
  <Descriptions.Item label="Dernière révision" span={1}>
    {new Date(selectedRequest.reviewedAt).toLocaleString("fr-FR")}
  </Descriptions.Item>
)}

{selectedRequest.reviewedBy && (
  <Descriptions.Item label="Révisé par" span={1}>
    {selectedRequest.reviewedBy}
  </Descriptions.Item>
)}
            {selectedRequest.adminNotes && (
              <Descriptions.Item label="Notes administratives" span={2}>
                <div style={{ whiteSpace: "pre-wrap" }}>
                  {selectedRequest.adminNotes}
                </div>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>

      {/* Modal de mise à jour du statut */}
      <Modal
        title="Mettre à jour le statut"
        open={statusUpdateVisible}
        onOk={handleStatusSubmit}
        onCancel={() => setStatusUpdateVisible(false)}
        okText="Mettre à jour"
        cancelText="Annuler"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Statut"
            name="status"
            rules={[
              { required: true, message: "Veuillez sélectionner un statut" },
            ]}
          >
            <Select>
              {Object.entries(availableStatuses).map(([key, label]) => (
                <Option key={key} value={key}>
                  <Tag color={getStatusColor(key)} icon={getStatusIcon(key)}>
                    {label}
                  </Tag>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Notes administratives" name="adminNotes">
            <TextArea
              rows={4}
              placeholder="Ajoutez des notes internes sur cette candidature..."
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default InstructorRequestManager;